import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { TimeSeriesPoint } from '../../types';

interface LeadsTrendChartProps {
  data: TimeSeriesPoint[];
  loading: boolean;
}

export function LeadsTrendChart({ data, loading }: LeadsTrendChartProps) {
  // Defensive: ensure data is always an array
  const safeData = Array.isArray(data) ? data : [];

  const chartData = useMemo(() => {
    return safeData.map((point) => ({
      date: new Date(point.date).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit'
      }),
      leads: point.leads,
      conversions: point.conversions
    }));
  }, [safeData]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Tendência de Leads</CardTitle>
          <CardDescription>Leads e conversões por dia</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground">Carregando...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (safeData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Tendência de Leads</CardTitle>
          <CardDescription>Leads e conversões por dia</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            Nenhum dado disponível para o período selecionado
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tendência de Leads</CardTitle>
        <CardDescription>Leads e conversões por dia</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="date"
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
            />
            <YAxis
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px'
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="leads"
              name="Leads"
              stroke="hsl(217, 91%, 60%)"
              strokeWidth={2}
              dot={{ fill: 'hsl(217, 91%, 60%)', strokeWidth: 2 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="conversions"
              name="Conversões"
              stroke="hsl(142, 71%, 45%)"
              strokeWidth={2}
              dot={{ fill: 'hsl(142, 71%, 45%)', strokeWidth: 2 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
